import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, type Type } from '@angular/core';
import type { ConversationEvent } from '../../../../../core/api/generated/models/ConversationEvent';
import { EVENT_RENDERER_REGISTRY } from './event-renderer';

/**
 * Picks the renderer for an event from {@link EVENT_RENDERER_REGISTRY} and instantiates it
 * via `NgComponentOutlet`. Unknown event types fall back to the registry entry keyed by `'*'`.
 */
@Component({
  selector: 'app-event-renderer-host',
  imports: [NgComponentOutlet],
  template: `
    @if (resolved(); as entry) {
      <ng-container *ngComponentOutlet="entry; inputs: outletInputs()" />
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventRendererHostComponent {
  private readonly registry = inject(EVENT_RENDERER_REGISTRY);

  readonly event = input.required<ConversationEvent>();

  protected readonly resolved = computed<Type<object> | null>(() => {
    const eventType = this.event().eventType;
    const direct = this.registry.find((entry) => entry.eventType === eventType);
    if (direct) return direct.component;
    const fallback = this.registry.find((entry) => entry.eventType === '*');
    return fallback?.component ?? null;
  });

  protected readonly outletInputs = computed<Record<string, unknown>>(() => ({ event: this.event() }));
}
